import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowsLeftRight, House, UsersThree } from "@phosphor-icons/react";

const ROLE_LABEL = {
  owner: "Owner",
  editor: "Editor",
  viewer: "Viewer",
  executor: "Executor",
};

export default function MigrateAssetDialog({ open, onOpenChange, asset, onMigrated }) {
  const { user, households, activeLedgerId } = useAuth();
  const [target, setTarget] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Only writer ledgers (owner/editor) are valid targets, excluding the current ledger.
  const sourceLedgerId = asset?.ledger_id || activeLedgerId || user?.user_id;
  const personalEligible = sourceLedgerId !== user?.user_id;
  const householdTargets = households
    .filter((h) => h.id !== sourceLedgerId && (h.my_role === "owner" || h.my_role === "editor"));

  const submit = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      await api.post(
        `/assets/${asset.id}/migrate`,
        { target_ledger_id: target },
        { headers: { "X-Ledger-Id": sourceLedgerId } },
      );
      toast.success("Asset moved.");
      onOpenChange(false);
      onMigrated?.(target);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not move asset.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="migrate-asset-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Move asset.</DialogTitle>
          <DialogDescription className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
            Re-file this entry into a different ledger
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-xs text-[#5C5F58]">
            The asset record, its photos and documents, and any maintenance schedule will move with it.
            You must be an Owner or Editor on the destination ledger.
          </p>

          <div className="space-y-2" data-testid="migrate-target-options">
            {personalEligible && (
              <label className="flex items-start gap-3 p-3 border border-[#E0DDD6] hover:border-[#1A3622] cursor-pointer">
                <input
                  type="radio"
                  name="migrate-target"
                  value={user.user_id}
                  checked={target === user.user_id}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="migrate-option-personal"
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0A192F] font-medium flex items-center gap-2">
                    <House size={14} className="text-[#1A3622]" />
                    Personal Ledger
                  </div>
                  <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mt-0.5">
                    Private · Owner
                  </div>
                </div>
              </label>
            )}

            {householdTargets.map((h) => (
              <label
                key={h.id}
                className="flex items-start gap-3 p-3 border border-[#E0DDD6] hover:border-[#1A3622] cursor-pointer"
              >
                <input
                  type="radio"
                  name="migrate-target"
                  value={h.id}
                  checked={target === h.id}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid={`migrate-option-${h.id}`}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0A192F] font-medium flex items-center gap-2 truncate">
                    <UsersThree size={14} className="text-[#1A3622] shrink-0" />
                    <span className="truncate">{h.name}</span>
                  </div>
                  <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mt-0.5">
                    Household · {ROLE_LABEL[h.my_role] || h.my_role}
                  </div>
                </div>
              </label>
            ))}

            {!personalEligible && householdTargets.length === 0 && (
              <div
                data-testid="migrate-no-options"
                className="text-xs text-[#5C5F58] p-3 border border-dashed border-[#C9C4B8] bg-[#F9F8F6]"
              >
                You don&apos;t have write access on any other ledger. Create or join a household to enable migration.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E0DDD6]">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="migrate-cancel-btn"
              className="px-4 py-2 text-sm border border-[#E0DDD6] hover:border-[#1A3622] text-[#0A192F]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !target}
              data-testid="migrate-submit-btn"
              className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
            >
              <ArrowsLeftRight size={14} weight="bold" />
              {submitting ? "Moving…" : "Move asset"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
